"use client";

/**
 * Root-level error boundary.
 *
 * Fires when a client-side exception escapes every nearer error.tsx —
 * including errors in `app/layout.tsx`, segment layouts (which bubble to
 * the parent), and any segment that doesn't define its own error.tsx
 * (e.g. `(auth)`, `(onboarding)`).
 *
 * Without this file Next.js falls back to a hard-coded "Application
 * error: a client-side exception has occurred" message with no recovery
 * affordance and no error ID. With it, users see a proper UI with the
 * digest they can paste into support, and the error gets structured-
 * logged to the console for browser-DevTools debugging.
 *
 * **Required shape**: global-error.tsx replaces the root layout when
 * active, so it MUST render its own `<html>` and `<body>`. It also can't
 * rely on ThemeProvider, fonts, or design-system CSS being available
 * (the layout that mounts them may itself be the source of the crash).
 * Styles are inlined, fonts fall back to system stacks.
 *
 * Like the dashboard error.tsx, we re-throw NEXT_REDIRECT / NEXT_NOT_FOUND
 * signals so Next.js's router can perform the navigation rather than
 * stranding the user on a recovery screen.
 */

import { useEffect } from "react";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  if (
    error?.digest?.startsWith("NEXT_REDIRECT") ||
    error?.digest === "NEXT_NOT_FOUND" ||
    error?.digest === "NEXT_HTTP_ERROR_FALLBACK"
  ) {
    throw error;
  }

  useEffect(() => {
    if (typeof console !== "undefined") {
      console.error("[global-error] caught client-side exception", {
        message: error.message,
        digest: error.digest,
        name: error.name,
        stack: error.stack,
      });
    }
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          background:
            "radial-gradient(ellipse at 30% 0%, rgba(124,58,237,0.08), transparent 50%), radial-gradient(ellipse at 70% 100%, rgba(59,130,246,0.06), transparent 50%), #0a0a12",
          color: "rgba(255,255,255,0.95)",
          WebkitFontSmoothing: "antialiased",
        }}
      >
        <div
          style={{
            maxWidth: "480px",
            width: "100%",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "20px",
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              background: "rgba(245, 158, 11, 0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fbbf24",
              fontSize: "28px",
              lineHeight: 1,
            }}
          >
            !
          </div>

          <div>
            <h1
              style={{
                fontSize: "24px",
                fontWeight: 700,
                margin: "0 0 8px 0",
                letterSpacing: "-0.02em",
              }}
            >
              Something went wrong
            </h1>
            <p
              style={{
                fontSize: "15px",
                lineHeight: 1.5,
                color: "rgba(255,255,255,0.6)",
                margin: 0,
              }}
            >
              The app hit an unexpected error before it finished loading.
              Try reloading — if it keeps happening, ping support and
              reference the ID below.
            </p>
          </div>

          {error.digest && (
            <code
              style={{
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                fontSize: "12px",
                padding: "6px 12px",
                borderRadius: "6px",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                color: "rgba(255,255,255,0.6)",
              }}
            >
              ID: {error.digest}
            </code>
          )}

          <div style={{ display: "flex", gap: "10px" }}>
            <button
              type="button"
              onClick={() => reset()}
              style={{
                appearance: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: 600,
                padding: "10px 20px",
                borderRadius: "8px",
                background:
                  "linear-gradient(135deg, #7c3aed, #3b82f6)",
                color: "white",
                boxShadow:
                  "0 4px 12px rgba(124,58,237,0.25), 0 2px 4px rgba(0,0,0,0.2)",
              }}
            >
              Try again
            </button>
            <a
              href="/"
              style={{
                fontSize: "14px",
                fontWeight: 500,
                padding: "10px 20px",
                borderRadius: "8px",
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.12)",
                color: "rgba(255,255,255,0.85)",
                textDecoration: "none",
              }}
            >
              Back to home
            </a>
          </div>
        </div>
      </body>
    </html>
  );
}
